import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDashboardSlides } from './slides';

const AUTOPLAY_DELAY = 7000;

export const DashboardHero = () => {
    const slides = useDashboardSlides();
    const isSingleSlide = slides.length <= 1;

    const autoplay = useRef(
        Autoplay({
            delay: AUTOPLAY_DELAY,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
        }),
    );

    const plugins = useMemo(() => (isSingleSlide ? [] : [autoplay.current]), [isSingleSlide]);

    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop: true, watchDrag: !isSingleSlide },
        plugins,
    );

    const [selectedIndex, setSelectedIndex] = useState(0);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        onSelect();
        emblaApi.on('select', onSelect);
        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi, onSelect]);

    const scrollTo = useCallback(
        (index: number) => {
            if (emblaApi) emblaApi.scrollTo(index);
        },
        [emblaApi],
    );

    return (
        <section className="relative overflow-hidden rounded-2xl border border-border/80 bg-card/95 shadow-elevation-1 sm:rounded-3xl">
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                    {slides.map((slide) => {
                        const SlideComponent = slide.component;
                        return (
                            <div key={slide.id} className="min-w-0 flex-[0_0_100%]">
                                <SlideComponent />
                            </div>
                        );
                    })}
                </div>
            </div>

            {!isSingleSlide && (
                <div className="absolute bottom-2 right-4 flex gap-1 sm:bottom-3 sm:right-6">
                    {slides.map((slide, index) => (
                        <button
                            key={slide.id}
                            type="button"
                            onClick={() => scrollTo(index)}
                            className="group flex h-7 w-7 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                            aria-label={`Ir para slide ${index + 1}`}
                            aria-current={index === selectedIndex}
                        >
                            <span
                                className={`h-2 rounded-full transition-[width,background-color,box-shadow] duration-200 ease-out ${
                                    index === selectedIndex
                                        ? 'w-8 bg-primary shadow-sm'
                                        : 'w-2 bg-muted-foreground/30 group-hover:bg-muted-foreground/50'
                                }`}
                            />
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
};
